/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isNumber from 'lodash.isnumber';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import isUndefined from 'lodash.isundefined';
import moment from 'moment';
import {Moment, unitOfTime} from 'moment-timezone';
import {error} from '../logger/logger';
import {DateRange} from '../types/general';
import {DatePeriodType, DateRangeType} from '../types/services';
import {toFixed} from './NumberUtil';

export type DateType = (Date | Moment | string | number);
export type Precision = unitOfTime.Diff;
export type CurrentDayDiff = {
  isSameDay: boolean,
  diffDays: number;
};

export interface DateModularResult {
  from: Date;
  to: Date;
};

/**
* Defines the default, Globally defined date
* format: "YYYY-MM-DD HH:MM:SS AM/PM", e.g. 2020-12-24 09:50 AM
*/
export const GLOBAL_DATE_FORMAT = 'YYYY-MM-DD, hh:mm A';

/**
 * Defines the defualt format for date range ...
 */
export const DATE_RANGE_FORMAT = 'YYYY-MM-DD';

/**
* Defines the Locale specific date with slashes
* format: "Month/Day/Year, HH:MM:SS AM/PM", e.g 12/24/2020, 9:50 AM
*/
export const LOCALE_DATE_FORMAT = 'LL, LT';

/**
* Defines the alternate, Locale specific date
* format: "Month Day, Year, HH:MM:SS AM/PM", e.g December 24, 2020, 09:50 AM
*/
export const LOCALE_DATE_FORMAT_SLASHES = 'l, LT';

// defines the default date format...
export const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

// defines the manual date time input formats...
export const VALID_MANUAL_DATE_TIME_FORMATS = ['MM/DD/YYYY h:mm A', 'M/D/YYYY h:mm A'];

// defines the manual date input formats...
export const VALID_MANUAL_DATE_FORMATS = ['MM/DD/YYYY', 'M/D/YYYY'];

// defines the date format string specific to the environment.
export const DefaultDateFormats  = {
  US : 'MM/DD/YYYY',
  EU : 'DD/MM/YYYY'
};

// defines the default time format...
export const DEFAULT_TIME_FORMAT = 'hh:mm A';

/**
 * Provides a simple utility which determines if the `current` `Date` is before
 * the `reference` `Date`.
 *
 * ```typescript
 *   import {isBefore} from '@ion/core/src/utils/DateUtil';
 *
 *   if (isBefore(expiration)) {
 *     ...
 *   }
 * ```
 *
 * @param   reference The `Date` to compare against the current Date to determine
 *          if it is greater than the `current` date.
 * @param   current The `Date` to compare against the `reference` Date to determine
 *          if it is less than that of the `reference` date.
 * @returns `true` of the `current` Date occurred before the `reference` Date,
 *          otherwise, false.
 */
export const isBefore = (reference: Date, current = new Date()): boolean => (
  current.getTime() < reference.getTime()
);

/**
 * Provides the utility which formats the time in the stored prefered
 * time format. This can be over ride by the second param of this method.
 */
export const timeFormatter = (date: DateType, format = DEFAULT_TIME_FORMAT, useUtc = false): string => {
  if (useUtc) {
    return moment.utc(date).format(format);
  }
  return moment(date).format(format);
};

/**
 * Provides a utility which determines if two dates are within the same range
 * of each other based on the specified precision. This function is essentially
 * a facade around moment.isSame which simply provides defaults.
 *
 * ```typescript
 *
 *   isSame(date1, date2, 'hour'); // true if dates are within same hour ...
 *   isSame(date1, date2, 'minute'); // true if dates are within same minute ...
 *   isSame(date1, date2, 'second'); // true if dates are within same second ...
 *
 * ```
 */
export const isSame = (first: DateType, second: DateType, precision: Precision = 'day') => {
  return moment(first).isSame(second, precision);
};

/**
 * Provides the utility which formats the date according to the passing
 * format string else just formats the date by default format.
 */
export const dateFormatter = (date: DateType, format = DEFAULT_DATE_FORMAT): string => {
  return moment(date).format(format);
};

/**
 * Provides a utility which calculates the difference between two units of time
 * based on the precision passed as the third argument
 *
 * ```typescript
 *
 *   timeDiff(date1, date2, 'hours'); // 500
 *   timeDiff(date1, date2, 'days'); // 20.833
 *   timeDiff(date1, date2, 'weeks'); // 2.976
 *
 * ```
 */
export const timeDiff = (first: DateType, second: DateType, precision: Precision = 'days') => {
  return moment(first).diff(second, precision, true);
};

/**
 * Provides a utility which calculates the difference between two units of time
 * and returns a formatted. Returns at most a second degree of specificity
 * i.e.
 *
 * 4 days 13 hrs
 * 36 mins 12 s
 * 7 hrs 13 mins
 * [TODO ION-CORE]: Account for weeks/months/years
 *
 * ```typescript
 *    import {timeFormatted} from '@ion/core/src/utils/';
 *    import {translate} from '@ion/core/src/locale/locale';
 *
 *    timeFormatted(48, translate) // '48 s'
 *    timeFormatted(73, translate) // '1 min 13 s'
 *    timeFormatted(120, translate) // '2 mins'
 *    timeFormatted(378, translate) // '6 mins 18 s'
 *    timeFormatted(7854, translate) // '2 hrs 10 min'
 *    timeFormatted(282744, translate) // '3 days 6 hrs'
 * ```
 */
export const timeFormatted = (time: number, translate: Function) => {
  const formattedDiff = [];
  if (time >= 60) {
    const minutes = time / 60;
    const minutesRounded = Math.floor(minutes);
    if (minutesRounded) {
      formattedDiff.unshift(translate(`common:time.minute${minutesRounded === 1 ? '' : 's'}Label`, {minutes: minutesRounded}));
    }
    const minuteSeconds = minutes % 1;
    if (minuteSeconds > 0.01 && minutes < 60) {
      const roundedSeconds = Math.floor(minuteSeconds * 60);
      if (roundedSeconds) {
        formattedDiff.splice(1, 0, `${translate('common:time.secondsLabel', {seconds: roundedSeconds})}`);
      }
    }
    if (minutes >= 60) {
      const hours = minutes / 60;
      formattedDiff.unshift(translate('common:time.hoursLabel', {hours: Math.floor(hours)}));
      const hourMinutes = hours % 1;
      if (hourMinutes > 0.01 && hours < 60) {
        const roundedMinutes = Math.floor(hourMinutes * 60);
        if (roundedMinutes) {
          formattedDiff.splice(1, 0, `${translate('common:time.minutesLabel', {minutes: roundedMinutes})}`);
        }
      }
      if (hours >= 24) {
        const days = hours / 24;
        formattedDiff.unshift(translate('common:time.daysLabel', {days: Math.floor(days)}));
        const dayHours = days % 1;
        if (dayHours > 0.01) {
          const roundedHours = Math.floor(dayHours * 60);
          if (roundedHours) {
            formattedDiff.splice(1, 0, `${translate('common:time.minutesLabel', {hours: roundedHours})}`);
          }
        }
      }
    }
  } else {
    formattedDiff.push(`${time} s`);
  }
  if (formattedDiff.length > 2) {
    formattedDiff.splice(2, formattedDiff.length - 2);
  }
  return formattedDiff.join(' ');
};

/**
 * Provides a simple utility which returns a new Date at the beginning of the
 * given date (if provided), otherwise returns a new Date at the beginning of
 * the current date.
 *
 * ```typescript
 *   import {startOfDay} from '@ion/core/src/utils/';
 *
 *   const timestamp = 'Thu Aug 08 2020 02:52:32 GMT-0400';
 *   startOfDay(timestamp); // Sat Aug 08 2020 00:00:00 GMT-0400 (Eastern Daylight Time)
 *
 * ```
 *
 */
export const startOfDay = (date?: DateType): Moment => {
  return moment(date).startOf('day');
};

/**
 * Provides a simple utility which returns a new Date at the end of the
 * given date (if provided), otherwise returns a new Date at the end of
 * the current date.
 *
 * ```typescript
 *   import {endOfDay} from '@ion/core/src/utils/';
 *
 *   const timestamp = 'Sun Aug 09 2020 00:00:00;
 *   endOfDay(timestamp); // 'Sun Aug 09 2020 23:59:59' GMT-0400 (Eastern Daylight Time)
 *
 * ```
 *
 */
export const endOfDay = (date?: DateType): Moment => {
  return moment(date).endOf('day');
};

/**
 * Provides a simple utility which returns a new Date at the beginning of the
 * next day from the given date (if provided), otherwise returns a new Date at
 * the beginning of the next day from the current date.
 *
 * ```typescript
 *   import {nextDay} from '@ion/core/src/utils/';
 *
 *   const timestamp = 'Tue Aug 18 2020 00:00:00 GMT-0400 (Eastern Daylight Time)';
 *   nextDay(timestamp); // Wed Aug 19 2020 00:00:00 GMT-0400 (Eastern Daylight Time)
 *
 * ```
 *
 */
export const nextDay = (date?: DateType): Moment => {
  return moment(date).startOf('day').add(1, 'days');
};

/**
 * Utility which takes a date object and returns
 * the time in 12 hour format with AM/PM flag
 *
 * ```typescript
 *   import {twelveHourTime} from '@ion/core/src/utils/';
 *
 *   const timestamp = 1597042849000;
 *   twelveHourTime(timestamp); // 12:00:49 AM
 *
 * ```
 *
 */
export const twelveHourTime = (date: DateType, format = 'h:mma'): string => {
  return moment(date).format(format);
};

/**
 * Utility which takes a date in object and returns the minutes
 * reading of the current time
 *
 * ```typescript
 *   import {timeInMinutes} from '@ion/core/src/utils/';
 *
 *   const timestamp = 159704284
 *   timeInMinutes(timestamp); // 44
 *
 * ```
 *
 */
export const timeInMinutes = (date: DateType): string => {
  return moment(date).format('m');
};

/**
 * Utility which returns the start of currrent day and
 * and end of current day as range values.
 *
 * ```typescript
 *   import {today} from '@ion/core/src/utils/';
 *
 *   today(); { fromDate: [start of current day], endDate: [end of current day] }
 *
 * ```
 */
export const today = () => {
  const now      = moment();
  const fromDate = now.startOf('day').toString();
  const toDate   = now.endOf('day').toString();
  return {
    fromDate,
    toDate
  };
};

/**
 * Utility which returns the boolean value true if
 * the input date is greater than today else false.
 *
 * ```typescript
 *
 *  import {isFutureDay} from '@ion/core/src/utils/';
 *
 *  const isFutureDay1 = isFutureDay(today); // false;
 *  const isFutureDay2 = isFutureDay(tomorrow); // true;
 *
 * ```
 * @param date input date to check
 * @returns true if the input date is greater than today else false
 */
export const isFutureDay = (date: DateType): boolean => {
  const endOfToday = moment().endOf('day');
  return moment(date) > endOfToday;
};

/**
 * Utility which returns the start and end of the day of the time that
 * is passed as an argument.
 *
 * ```typescript
 *   import {getDayRange} from '@ion/core/src/utils/';
 *
 *   getDayRange('2021-07-08T17:17:07.266Z'); // { fromDate: [start of July 8th], endDate: [end of July 8th] }
 *
 * ```
 */
export const getDayRange = (time, format = 'MM/DD/YYYY') => {
  const date     = moment(time);
  const fromDate = date.startOf('day').format(format);
  const toDate   = date.endOf('day').format(format);
  return {
    fromDate,
    toDate
  };
};

/**
 * Utility which returns the start of previous day and
 * and end of previous day as range values.
 *
 * ```typescript
 *   import {getYesterday} from '@ion/core/src/utils/';
 *
 *   getYesterday(); { fromDate: [start of previous day], endDate: [end of previous day] }
 *
 * ```
 */
export const getYesterday = (toString: boolean = true) => {
  const fromDate = moment().subtract(1, 'days').startOf('day');
  const toDate   = moment().subtract(1, 'days').endOf('day');
  return {
    fromDate : toString ? fromDate.toString() : fromDate,
    toDate   : toString ? toDate.toString() : toDate
  };
};

/**
 * Utility which returns a date range with start date as days
 * before today and end date as today, or if adding, start date as today
 * and toDate teh days after today.
 *
 * ```typescript
 *   import {getDaysFromToday} from '@ion/core/src/utils/';
 *
 *   getDaysFromToday(); { fromDate: [start of previous day], endDate: [end of previous day] }
 *
 * ```
 */
export interface DaysFromTodayArgs {
  stringify?: boolean;
  fromToday?: boolean;
}

export const getDaysFromToday = (days, direction: 'subtract' | 'add' = 'add', args: DaysFromTodayArgs = {stringify: false, fromToday: false}) => {
  days = days-(args.fromToday ? 1 : 0);
  const startDate = moment().subtract(args.fromToday ? 0 : 1, 'days');
  const endDate   = moment()[direction](days, 'days');
  const fromDate  = (direction === 'subtract' ? endDate : startDate).startOf('day');
  const toDate    = (direction === 'subtract' ? startDate : endDate).endOf('day');
  return {
    fromDate : args.stringify ? fromDate.toString() : fromDate,
    toDate   : args.stringify ? toDate.toString() : toDate
  };
};

/**
 * It will return last 24 hours dateTime from current time
 * ```typescript
 *
 * import {last24Hours} from '@ion/core/src/utils/';
 *
 * last24Hours();
 *
 * ```
 */
export const last24Hours = () => {
  return moment().subtract(24, 'hours');
};

/**
 * Utility which returns dateTime from current time based on
 * substracted hours
 * ```typescript
 *
 * import {getDateFromToday} from '@ion/core/src/utils/';
 *
 * getDateFromToday(2); { fromDate: [current dt - hours], endDate: [current date time] }
 *
 * ```
 */
export const getDateFromToday = (hours: number) => {
  const fromDate = moment().subtract(hours, 'hours');
  const toDate   = moment();
  return {fromDate, toDate};
};

/**
 * Utility which returns the start of currrent week and
 * and end of current week as range values.
 *
 * ```typescript
 *   import {lastWeek} from '@ion/core/src/utils/';
 *
 *   lastWeek(); { fromDate: [start of current week], endDate: [end of current week] }
 *
 * ```
 */
export const lastWeek = () => {
  const now      = moment().subtract(1, 'weeks');
  const fromDate = now.startOf('week').toString();
  const toDate   = now.endOf('week').toString();
  return {
    fromDate,
    toDate
  };
};

/**
 * Utility which returns the start of currrent month and
 * and end of current month as range values.
 *
 * ```typescript
 *   import {lastMonth} from '@ion/core/src/utils/';
 *
 *   lastMonth(); { fromDate: [start of current month], endDate: [end of current month] }
 *
 * ```
 */
export const lastMonth = () => {
  const now      = moment().subtract(1, 'months');
  const fromDate = now.startOf('month').toString();
  const toDate   = now.endOf('month').toString();
  return {
    fromDate,
    toDate
  };
};

/*
 * Utility which takes a date in a DateType format and
 * returns the date in MM/DD/YYYY US calendar format
 *
 * ```typescript
 *   import {calendarDate} from '@ion/core/src/utils/DateUtil';
 *
 *   const timestamp = "2020-08-19T01:04:44.000Z"
 *   calendarDate(timestamp); // '8/18/2020'
 *
 * ```
 *
 */
export const calendarDate = (date: DateType, format = 'MM/DD/YYYY'): string => {
  return date ? moment(date).format(format) : '';
};

/**
 * Given a valid date and optional format, returns the formatted date
 * for the given format, either the Global format, or the Local format;
 * i.e. YYYY-MM-DD HH:MM:SS AM/PM, or 'Month Day, Year, HH:MM:SS AM/PM'
 * (defaults to Global).
 *
 * Global Format : 'YYYY-MM-DD HH:MM:SS AM/PM',
 * Local Format  : 'Month Day, Year, HH:MM:SS AM/PM'
 *
 * ```typescript
 *   import {formatDateTime} from '@ion/core/src/utils/DateUtil';
 *
 *  const date = '2018-03-09T09:50:32.953Z';
 *
 *  // format date string based on default global format ...
 *  formatDateTime(date); // '2018-03-09, 03:20 PM'
 *
 *  // format date string based on default global format and include seconds ...
 *  formatDateTime(date, true, true); // '2018-03-09, 03:20:32 PM'
 *
 *  // format date string based on alternate locale format ..
 *  formatDateTime(date, false); // 'March 9, 2018, 3:20 PM'
 *
 *  // format date string based on alternate locale format and include seconds ...
 *  formatDateTime(date, false, true); // 'March 9, 2018, 3:20:32 PM'
 *
 * ```
 */
export const formatDateTime = (date: DateType, useGlobalFormat = true, includeSeconds = false) => {
  if (date) {
    let format = useGlobalFormat ? GLOBAL_DATE_FORMAT : LOCALE_DATE_FORMAT;
    if (includeSeconds) {
      format = useGlobalFormat ? format.replace('mm', 'mm:ss') : `${format}S`;
    }
    return moment(date).format(format);
  }
  return '';
};

/**
 * Given a valid date and optional format, returns the formatted date
 * for the given format, either the Locale format, or the Local format
 * with slashes
 * i.e. 'Month Day, Year, HH:MM:SS AM/PM' or 'Month/Day/Year, HH:MM:SS AM/PM'
 * (defaults to Local without slashes).
 *
 * Local Format            : 'Month Day, Year, HH:MM:SS AM/PM'
 * Local Format w/ slashes : 'Month/Day/Year, HH:MM:SS AM/PM'
 *
 * ```typescript
 *   import {formatDateTimeToLocale} from '@ion/core/src/utils/DateUtil';
 *
 *  const date = '2018-03-09T09:50:32.953Z';
 *
 *  // format date string based on default locale format ...
 *  formatDateTime(date); // 'March 9,2018, 3:20 PM'
 *
 *  // format date string based on alternate locale format and include seconds ...
 *  formatDateTime(date, false, true); // 'March 9, 2018, 3:20:32 PM'
 *
 *  // format date string based on locale format with slashes and include seconds ...
 *  formatDateTime(date, true, true); // '3/9/2018, 3:20:32 PM'
 *
 *  // format date string based on locale format with slashes ..
 *  formatDateTime(date, true, false); // '3/9/2018, 3:20 PM
 *
 * ```
 */
export const formatDateTimeToLocale = (date: DateType, slashes = false, includeSeconds = false) => {
  if (date) {
    let format = slashes ? LOCALE_DATE_FORMAT_SLASHES : LOCALE_DATE_FORMAT;
    if (includeSeconds) {
      format = `${format}S`;
    }
    return moment(date).format(format);
  }
  return '';
};

/**
 * Utility which takes a string and formats it to time (h:mma).
 *
 * ```typescript
 *   import {formatTime} from '@ion/core/src/utils/';
 *
 *   const time = '20:25:45';
 *   formatTime(time); // 8:25pm
 *
 * ```
 *
 */
export const formatTime = (date: DateType, format = 'h:mma'): string => {
  return moment(date, 'hh:mm').format(format);
};

/**
 * Given a valid date & time, converts the utc to local format
 *
 * ```typescript
 *   import {formatTimeToLocale} from '@ion/core/src/utils/';
 *
 *   const formatLocalTime = formatTimeToLocale("2022-01-05T06:38:55.591Z", "12:08:00");
 */
export const formatTimeToLocale = (date: string, time: string): string => {
  const startDate       = formatDateTime(date);
  const startDateFormat = startDate.slice(0, 10) + ' ';
  const dateFormat      = startDateFormat.concat(time);
  return moment.utc(dateFormat).local().format('HH:mm:ss');
};

/**
 * Given a time unit in the form 'HH:mm:ss', parses the time to a Date Object.
 */
export const timeToDate = (time: string, token: string = ':'): Date => {
  if (time) {
    const [h, m, s] = time.split(token);
    return moment({h: Number(h), m: Number(m), s: Number(s)}).toDate();
  }
  return null;
};

/**
 * Given a Date Object, formats a time unit (default = 'HH:mm:ss').
 */
export const dateToTime = (date: DateType, format: string = 'HH:mm:ss'): string => {
  return date ? moment(date).format(format) : null;
};

/**
 * Given a Time String, formats a time unit (default = 'HH:mm:ss').
 */
export const formatTimeString = (date: DateType, format: string = 'HH:mm:ss'): string => {
  return date ? moment(date, 'hh:mm A').format(format) : null;
};

/**
 * Utility which takes date string and returns ISO Time at current, start,
 * or end of the day.
 *
 * ```typescript
 *   import {toISOString} from '@ion/core/src/utils/';
 *
 *   const date = '2010-07-31';
 *   toISOString(date, 'startOf'); // '2010-07-31T07:00:00.000Z'
 *
 * ```
 */
export const toISOString = (date: string | number | Date, timeOfDay: 'endOf' | 'startOf' | 'current' = 'current', inputFormat?: string): string => {
  if (date) {
    const formattedDate = inputFormat ? date : new Date(date);
    if (timeOfDay === 'current') {
      return moment(formattedDate, inputFormat).toISOString();
    }
    return moment(formattedDate, inputFormat)[timeOfDay]('day').toISOString();
  }
};

/**
 * Utility which takes a date string and returns an ISO string which will
 * convert back to the same date across timezones.
 *
 * ```typescript
 *   import {toDateISOString} from '@ion/core/src/utils/';
 *
 *   const date = '2010-07-31';
 *   toDateISOString(date); // '2010-07-31T12:00:00.000Z'
 *
 * ```
 */
export const toDateISOString = (date: string | number | Date, time: Boolean = false, fullrange = false): string => {
  if (date) {
    let d = new Date(date);
    if (!d.getDate()) {
      d = new Date(date.toString().replace(/-/g, '/'));
    }
    if (fullrange) {
      return moment.utc([
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        time ? d.getHours() : 12,
        time ? d.getMinutes() : 0, 0, 0
      ]).toISOString();
    }
    return moment.utc([d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0]).toISOString();
  }
};

/**
 * Converts a Date object to a new Date instance localized to the given timezone.
 *
 * ```typescript
 *   import {toTimeZone} from '@ion/core/src/utils/';
 *
 *   // display an Asset's lastModifiedOn value in the users timezone ...
 *   const lastModifiedOnUI = toTimeZone(asset.lastModifiedOn, user.timeZone);
 *
 * ```
 *
 * @param   date The Date instance to be converted to the given timezone.
 * @param   timezone The timezone to which the Date is to be converted.
 * @param   format An optional format which, when specified, will return the
 *          converted Date as a formatted string based on this value.
 * @returns The provided Date converted to the given timezone.
 */
export const toTimeZone = (date: DateType, timezone: string, format?: string): Date | string => {
  const converted = new Date(moment.tz(date, 'UTC').tz(timezone).format('YYYY/MM/DD HH:mm:ss'));
  return format ? moment.tz(converted, 'UTC').tz(timezone).format(format) : converted;
};

/**
 * Converts a Date object to a new Date instance localized to the given timezone utcOffset.
 *
 * ```typescript
 *   import {toTimeZoneUtcOffset} from '@ion/core/src/utils/';
 *
 *   // display an Asset's lastModifiedOn value in the users timezone ...
 *   const userTimeZoneOffset = moment.tz(user.timeZone).utcOffset();
 *   const lastModifiedOnUI = toTimeZoneUtcOffset(asset.lastModifiedOn, userTimeZoneOffset);
 *
 * ```
 *
 * @param   date The Date instance to be converted to the given timezone.
 * @param   timezone The timezone to which the Date is to be converted.
 * @param   format An optional format which, when specified, will return the
 *          converted Date as a formatted string based on this value.
 * @returns The provided Date converted to the given timezone.
 */

export const toTimeZoneUtcOffset = (
  date: DateType,
  timezone: number,
  format?: string
): Date | string => {
  const converted = new Date(moment.utc(date).utcOffset(timezone).format('YYYY/MM/DD HH:mm:ss'));
  return format ? moment.utc(date).utcOffset(timezone).format(format) : converted;
};

/**
 * Given a moment.js parsable date time value, returns true if the date
 * can be parsed as a valid JavaScript date object, otherwise, returns
 * false.
 *
 */
export const isValidDateTime = (val, format: string | string[] = 'YYYY-MM-DDTHH:mm:ss.sssZ') => {
  return moment(val, format, true).isValid();
};

/**
 * The utility validates the given data and returns a boolean indicating
 * whether the data is valid, otherwise, false.
 *
 * ```typescript
 *
 *  import {isValid} from '@ion/core/src/utils/';
 *
 *  const valid   = isValid(new Date()); // returns true
 *  const inValid = isValid('2013-01-'); // returns false
 *
 * ```
 *
 */
export const isValid = (date: string | Date): boolean => moment(date).isValid();

/**
 * Given a moment.js parsable date value, returns formatted value if the date
 * can be parsed as a valid JavaScript date object, otherwise, returns
 * the value.
 *
 */
export const formatIfValid = (val, format = 'L') => {
  return moment(val).isValid() ? moment(val).format(format) : val;
};

/**
 * Takes a valid date to be checked and lower date
 * Will return True if given date is same or after the lower date else will return false
 *
 * * ```typescript
 *
 *  import {isSameOrAfter} from '@ion/core/src/utils/';
 *
 *  const isSameOrAfter   = isSameOrAfter(new Date()); // returns true
 *  const isSameOrAfter   = isSameOrAfter('1969-01-01T19:00:00Z'); // returns false
 *
 * ```
 *
 */
export const isSameOrAfter = (date: string | Date, lowerDate = '1970-01-01T19:00:00Z'): boolean => {
  if (isValid(date)) {
    return moment(date).isSameOrAfter(moment(lowerDate), 'second');
  }
  return false;
};

/**
 * Takes either a valid Date object or a generic object with
 * Moment values and converts all values (or passed value) to a Moment
 *
 */
export const dateToMoment = (val: Date | {[key: string]: Date | string}) => {
  if (isObject(val)) {
    Object.entries(val).forEach(([key, item]) => {
      val[key] = moment(item);
    });
    return val;
  }
  return moment(val);
};

/**
 * Returns a date in the past that is the number of days away
 * that is passed as an argument
 *
 */
export const getMinDate = (daysFrom: number, date = moment()): Moment => {
  if (!isUndefined(daysFrom)) {
    return date.subtract(daysFrom, 'days').startOf('day');
  }
};

/**
 * Returns a date in the future that is the number of days away
 * that is passed as an argument
 *
 */
export const getMaxDate = (daysFrom: number, date = moment()): Moment => {
  if (!isUndefined(daysFrom)) {
    return date.add(daysFrom, 'days').endOf('day');
  }
};

/**
 * Returns the startDate and endDate for last week ...
 *
 * ```typescript
 *
 * import {getWeekRange} from '@ion/core/src/utils/';
 * const {startDate, endDate} = getWeekRange();
 *
 * ```
 *
 * @returns {startDate, endDate}
 */
export const getWeekRange = (date: Moment = moment(),
  format: string = GLOBAL_DATE_FORMAT): DateRangeType => {
  const endDate   = date.endOf('day').format(format);
  const startDate = date.subtract(6, 'days').startOf('day').format(format);
  return {startDate, endDate};
};

/**
 * Returns the startDate and endDate for last month ...
 *
 * ```typescript
 *
 * import {getMonthRange} from '@ion/core/src/utils/';
 * const {startDate, endDate} = getMonthRange();
 *
 * ```
 *
 * @returns {startDate, endDate}
 */
export const getMonthRange = (date: Moment = moment(),
  format: string = GLOBAL_DATE_FORMAT): DateRangeType => {
  const prevMonth = date.subtract(1, 'months');
  const startDate = prevMonth.startOf('month').format(format);
  const endDate   = prevMonth.endOf('month').format(format);
  return {startDate, endDate};
};

/**
 * Returns the startDate and endDate for last year ...
 *
 * ```typescript
 *
 * import {getYearRange} from '@ion/core/src/utils/';
 * const {startDate, endDate} = getYearRange();
 *
 * ```
 *
 * @returns {startDate, endDate}
 */
export const getYearRange = (date: Moment = moment(),
  format: string = GLOBAL_DATE_FORMAT): DateRangeType => {
  const prevYear  = date.subtract(1, 'years');
  const startDate = prevYear.startOf('year').format(format);
  const endDate   = prevYear.endOf('year').format(format);
  return {startDate, endDate};
};

/**
 * Utility function which would invoke one of the date range methods
 * getWeekRange, getMonthRange, getYearRange based on the DateRange
 * passed and returns the respective result of invocation.
 *
 * @param range the current range for which the api is invoked
 */
export const getDateRange = (
  period: DateRange,
  date: Moment = moment(),
  format = GLOBAL_DATE_FORMAT
): DatePeriodType => {
  const api = {
    [DateRange.Week]  : getWeekRange,
    [DateRange.Month] : getMonthRange,
    [DateRange.Year]  : getYearRange
  };
  const dateRange = api?.[period]?.(date, format);
  return {...dateRange, period};
};

/**
 * Utility which takes a number format of the date that is the total count of the days from Dec 30, 1899
 * and returns the date which is equivalent to the ISO string format of a given input.
 *
 * ```typescript
 *   import {xlsxDatetoISOString} from '@ion/core/src/utils/';
 *
 *   const daysCount = 44260;
 *   xlsxDatetoISOString(daysCount); // '2021-03-05T12:00:00.000Z'
 *
 * ```
 *
 * @param   daysCount Total count of the days from Dec 30, 1899
 * @returns ISO string format of the day's count
 */
export const xlsxDatetoISOString = (daysCount: number): string => (
  toDateISOString(xlsxDatetoJSDate(daysCount))
);

/**
 * Utility which takes a number format of the date that is the total count of the days from Dec 30, 1899
 * and returns the date which is equivalent to the JS date object.
 *
 * ```typescript
 *   import {xlsxDatetoJSDate} from '@ion/core/src/utils/';
 *
 *   const daysCount = 44260;
 *   xlsxDatetoJSDate(daysCount); // '2021-03-05T12:00:00.000Z'
 *
 * ```
 */
export const xlsxDatetoJSDate = (daysCount: number) => {
  const noOfDaysToSubtract = new Date().getTimezoneOffset() <= 0 ? 25569 : 25568;
  const daysInUTC     = Math.floor(daysCount - noOfDaysToSubtract);
  const utcValue      = daysInUTC * 86400;
  const dateInfo      = new Date(utcValue * 1000);
  const fractionalDay = daysCount - Math.floor(daysCount) + 0.0000001;
  let totalSeconds    = Math.floor(86400 * fractionalDay);
  const seconds       = totalSeconds % 60;
  totalSeconds        -= seconds;
  const hours         = Math.floor(totalSeconds / (60 * 60));
  const minutes       = Math.floor(totalSeconds / 60) % 60;

  return new Date(dateInfo.getFullYear(),
    dateInfo.getMonth(),
    dateInfo.getDate(),
    hours, minutes, seconds);
};

/**
 * The utility parse xlsx date input string to local JS date object
 * with targeted timezone.
 *
 * ```typescript
 *
 *  import {xlsxDateParser} from '@ion/core/src/utils/';
 *
 *  const now = xlsxDateParser(44260); // xlxs date input
 *  const now = xlsxDateParser(new Date()); // date input
 *
 * ```
 */
export const xlsxDateParser = (date: Date | string | number) => {
  let input: any = date;
  if (isNumber(date)) {
    input = xlsxDatetoJSDate(date as number);
  }
  return safeDateParser(input);
};

/**
 * Utility which returns current date in a pre-defined format ...
 */
export const getCurrentDate = (): string => {
  const today = new Date();
  const dd = today.getDate();
  const mm = today.getMonth() + 1;
  const yyyy = today.getFullYear();
  let day;
  let month;

  if (dd < 10) {
    day = `0${dd}`;
  } else {
    day = dd;
  }
  if (mm < 10) {
    month = `0${mm}`;
  } else {
    month = mm;
  }

  const currentDate = `${month}/${day}/${yyyy}`;

  return currentDate;
};

/**
 * Utility which returns current date and time in a pre-defined format ...
 */
export const getCurrentDateTime = (): string => {
  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour12 : true,
    hour   : 'numeric',
    minute : 'numeric'
  });
  return getCurrentDate() + ' ' + currentTime;
};

/**
 * Provides a simple utility which converts unix timestamps (seconds since
 * unix epoch) by multiplying the value by 1000 (to convert from seconds to
 * milliseconds), and returning a new `Date` based on the conversion.
 *
 * ```typescript
 *  import {fromUnixTimeStamp} from '@ion/core/src/utils/DateUtil';
 *
 *  const date = fromUnixTimeStamp(2003545600);
 *  // October 17, 2033, at 12:00 AM
 * ```
 *
 * @param   timestamp Unix timestamp which is to be converted to a `Date`.
 * @returns new `Date` based on the provided unix timestamp.
 */
export const fromUnixTimeStamp = (timestamp: number): Date => (
  new Date(timestamp * 1000)
);

/**
 * Converts Date object to unix time
 */
export const parseDateToUnix = (date: Date) => {
  return moment(date).valueOf();
};

/**
 * Utility to convert seconds into Hour value
 *
 * ```typescript
 *   import {secondsToHours} from '@ion/core/src/utils/';
 *
 *   secondsToHours(14561629); // 4044.9
 *   secondsToHours(14673797); // 4076.1
 *   secondsToHours(14673797, 2); // 4076.05
 *
 * ```
 *
 * @param  value Seconds value to be convert to Hour
 * @param  digits Returning decimal digit
 */
export const secondsToHours = (value: number, digits = 1): number => {
  return toFixed((value / 3600), digits);
};

/**
 *  Formats the passed date value by separating the calendar date and time
 *  based on the passed separator and order.
 *
 * ```typescript
 *  import {secondsToHours} from '@ion/core/src/utils/';
 *
 *  dateTimeSeparator({
 *    value: '2021-08-27T10:16:21.000Z',
 *    separator: '-',
 *    dateFormat = 'LL',
 *    timeFormat = 'h:mma'
 *  }) // August 27, 2021 - 8:16pm
 * ```
 */
export const dateTimeSeparator = ({
  value,
  separator = '|',
  dateFormat = 'LL',
  timeFormat = 'h:mma',
  timeFirst = false
}) => {
  const dateDisplay   = calendarDate(value, dateFormat);
  const timeDisplay   = formatTime(value, timeFormat);
  const firstDisplay  = timeFirst ? timeDisplay : dateDisplay;
  const secondDisplay = timeFirst ? dateDisplay : timeDisplay;
  return `${firstDisplay} ${separator} ${secondDisplay}`;
};

/**
 * Provides a utility which returns current day, yesterday days difference.
 */
export const getCurrentDayDifference = (time: string): CurrentDayDiff => {
  const now = new Date();
  const beginningOfDay = startOfDay(now);
  const isSameDay = isSame(now, time, 'day');
  const diffDays = Math.ceil(timeDiff(beginningOfDay, time, 'days'));
  return {
    isSameDay,
    diffDays
  };
};

/**
 * Utility to retrieve hours of a native JS date
 * while accounting for the current timezone.
 */
export const getHours = (value: Date, format: string = 'h'): number | string => {
  const m = moment(value);
  return format ? m.format(format) : m.hours();
};

/**
 * Utility to retrieve minutes of a native JS date
 * while accounting for the current timezone.
 */
export const getMinutes = (value: Date, format: string = 'mm'): number | string => {
  const m = moment(value);
  return format ? m.format(format) : m.minutes();
};

/**
 * Utility to retrieve day of a native JS date
 * while accounting for the current timezone.
 *
 * ```typescript
 *
 *  import {getDays} '@ion/core/src/utils/';
 *
 *  const days = getDays(new Date()); // returns date of today
 *
 * ```
 */
export const getDays = (value: Date, format: string = 'DD'): number | string => {
  const m = moment(value);
  return format ? m.format(format) : m.day();
};

/**
 * Utility to retrieve Meridiem of a native JS date
 * while accounting for the current timezone.
 */
export const getMeridiem = (value: Date): string => {
  return moment(value).format('A');
};

/**
 * Utility to retrieve the day and time of a given date and time
 */
export const getFormattedDays = (date: string, time: string): string => {
  const now        = moment(date).format('YYYY-MM-DD');
  const formatDate = new Date(now+' '+time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
  return formatDate?.toLocaleUpperCase();
};

/**
 * utility to retrieve date format while accounting
 * for the application running environemnt.
 */
export const getDefaultDateFormat = (env = DefaultDateFormats.US) => {
  return DefaultDateFormats[env] || DefaultDateFormats.US;
};

/**
 *  Provides a utility which returns Date when we pass time.
 *
 * ```typescript
 *   import {timetoDate} from '@ion/core/src/utils/DateUtil';
 *
 *   timetoDate(11:06 AM); // 2022-01-05T11:06:47.349Z
 *   timetoDate(05:06 PM); // 2022-01-05T05:06:47.349Z
 * ```
 *
 */
export const timetoDate = (time: string, date?: string, format = 'YYYY-MM-DD') => {
  const now = date || new Date();
  const currentDate = moment(now).format(format);
  const times = new Date(currentDate+' '+time);
  const result = times?.toISOString();
  return result;
};

/**
 * The utility converts date input string to local time string
 * with targeted timezone.
 *
 * ```typescript
 *
 *  import {resolveDateInput} from '@ion/core/src/utils/';
 *
 *  const now = resolveDateInput(new Date());
 *
 * ```
 */
export const resolveDateInput = (date: Date | string) => {
  // just converting `-` to `/` for safe date conversion in Firefox...
  const validDate    = isString(date) ? (date as string).replace(/-/g, '/') : date;
  const targetTime   = new Date(validDate);
  const momentOffset = moment(validDate).utcOffset() / 60;
  const systemOffset = targetTime.getTimezoneOffset() / 60;
  const offset       = -systemOffset * 2 + -(momentOffset);
  const tzDifference = offset * 60 + targetTime.getTimezoneOffset();
  return new Date(targetTime.getTime() + tzDifference * 60 * 1000);
};

/**
 * The utility parse date input string to local time string
 * with targeted timezone.
 *
 * ```typescript
 *
 *  import {safeDateParser} from '@ion/core/src/utils/';
 *
 *  const now = safeDateParser(new Date());
 *
 * ```
 */
export const safeDateParser = (
  date: Date | string,
  isLocalTime = true,
  isISOStringOut = true
) => {
  try {
    if (isValid(date)) {
      const input = isLocalTime ? resolveDateInput(date) : date;
      return isISOStringOut ? toISOString(input) : input;
    }
  } catch (err) {
    error(err);
  }
  return null;
};

export const modularDateFormatter = ({from, to}: DateModularResult) => '[' + from + ' TO ' + to + ']';

/**
 * Given a date range this function will return an API-ready date-range query string.
 */
export const convertModularSearchDateRange = (date?: string, format = 'YYYY-MM-DD', formatter = modularDateFormatter) => {
  const [start, end] = date?.split(' - ') || [];
  const from         = start ? moment(start, 'M/D/YYYY').startOf('day') : moment().subtract(14, 'days');
  const to           = end ? moment(end, 'M/D/YYYY').endOf('day') : moment().add(1, 'days');
  const result = {
    from : from.format(format),
    to   : to.format(format)
  };
  return formatter?.(result) || result;
};

/**
 * Given an eventTime, this function will return an API-ready date-range query string.
 */
export const convertToUTCDateString = (eventTime) => {
  return moment(Number(eventTime)).toISOString();
};

/**
 * Given a date string and duration in minutes, this function will return an API-ready date-range query string.
 */
export const convertToUTCDateStringWithDuration = (eventTime: string, durationInMinutes: number): string => {
  return moment(eventTime)
    .add(durationInMinutes, 'minutes')
    .toISOString();
};
